import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Upload, Wand2, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inp, pill, ROBOTS, TYPES, Score } from "./shared";
import PageEditor from "./PageEditor";

const STATUS = [["", "Any SEO status"], ["issues", "Has issues"], ["title", "Missing/weak title"], ["desc", "Missing description"],
  ["og_image", "Missing OG image"], ["noindex", "Noindex"], ["custom", "Manually customised"]];

function BulkBar({ keys, onDone }) {
  const [robots, setRobots] = useState("");
  const [field, setField] = useState("og_image");
  const [val, setVal] = useState("");
  const run = async (fields) => {
    try { const { data } = await api.post("/admin/seo/bulk", { keys, fields }); toast.success(`Updated ${data.updated} page(s)`); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const gen = async () => {
    const overwrite = window.confirm("Also OVERWRITE manually customised values?\nOK = overwrite, Cancel = only fill missing fields");
    try { const { data } = await api.post("/admin/seo/generate", { keys, overwrite }); toast.success(`Generated for ${data.updated} page(s)`); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl border border-primary/40 bg-primary/5 mb-3" data-testid="seo-bulk-bar">
      <span className="text-sm font-semibold">{keys.length} selected</span>
      <select className={`${inp} w-44`} value={robots} onChange={(e) => setRobots(e.target.value)} data-testid="seo-bulk-robots">
        <option value="">Set indexing…</option>{ROBOTS.map((r) => <option key={r}>{r}</option>)}</select>
      <button disabled={!robots} onClick={() => run({ robots })} className={`${pill} border border-border`} data-testid="seo-bulk-robots-apply">Apply</button>
      <select className={`${inp} w-40`} value={field} onChange={(e) => setField(e.target.value)} data-testid="seo-bulk-field">
        {["og_image", "twitter_image", "keywords", "title", "description", "schema_type"].map((x) => <option key={x}>{x}</option>)}</select>
      <input className={`${inp} w-56`} value={val} onChange={(e) => setVal(e.target.value)} placeholder="value" data-testid="seo-bulk-value" />
      <button disabled={!val} onClick={() => run({ [field]: val })} className={`${pill} border border-border`} data-testid="seo-bulk-field-apply">Set field</button>
      <button onClick={gen} className={`${pill} bg-primary text-primary-foreground`} data-testid="seo-bulk-generate"><Wand2 className="h-4 w-4" /> Generate missing</button>
    </div>
  );
}

function CsvImport({ onDone }) {
  const [csv, setCsv] = useState("");
  const [prev, setPrev] = useState(null);
  const file = (e) => { const fl = e.target.files?.[0]; if (fl) fl.text().then(setCsv); };
  const preview = async () => { try { const { data } = await api.post("/admin/seo/import", { csv }); setPrev(data); } catch (e) { toast.error(apiError(e)); } };
  const apply = async () => { try { const { data } = await api.post("/admin/seo/import", { csv, apply: true }); toast.success(`Applied ${data.applied}, skipped ${data.skipped}`); setPrev(null); setCsv(""); onDone(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-xl border border-border p-4 mb-4 space-y-3" data-testid="seo-csv-import">
      <div className="flex items-center gap-3"><input type="file" accept=".csv,text/csv" onChange={file} data-testid="seo-csv-file" className="text-sm" />
        <button disabled={!csv} onClick={preview} className={`${pill} border border-border`} data-testid="seo-csv-preview">Validate & preview</button></div>
      {prev && <>
        <p className="text-sm">{prev.valid} row(s) ready · <span className="text-destructive">{prev.invalid} with errors</span></p>
        <div className="max-h-60 overflow-y-auto text-xs space-y-1">
          {prev.rows.filter((r) => Object.keys(r.changes).length || r.errors.length).map((r) => (
            <p key={r.line} className={r.errors.length ? "text-destructive" : ""}>Line {r.line} · {r.name || r.key}: {r.errors.length ? r.errors.join("; ") : Object.keys(r.changes).join(", ")}</p>
          ))}
        </div>
        <button disabled={!prev.valid} onClick={apply} className={`${pill} bg-primary text-primary-foreground`} data-testid="seo-csv-apply">Apply {prev.valid} change(s)</button>
      </>}
    </div>
  );
}

export default function PageManager({ presetType = "", presetStatus = "" }) {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState(presetType);
  const [status, setStatus] = useState(presetStatus);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const load = useCallback(() => api.get("/admin/seo/pages", { params: { type: type || undefined, status: status || undefined, q: q || undefined } }).then((r) => setRows(r.data)), [type, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exportCsv = async () => {
    const r = await api.get("/admin/seo/export.csv", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "ff-seo-export.csv"; a.click();
  };
  const all = rows.length > 0 && sel.length === rows.length;
  return (
    <div data-testid="seo-page-manager">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
          <input className={`${inp} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or URL" data-testid="seo-search" /></div>
        <select className={`${inp} w-36`} value={type} onChange={(e) => setType(e.target.value)} data-testid="seo-filter-type">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <select className={`${inp} w-48`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="seo-filter-status">{STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <button onClick={exportCsv} className={`${pill} border border-border`} data-testid="seo-export-csv"><Download className="h-4 w-4" /> Export CSV</button>
        <button onClick={() => setImp(!imp)} className={`${pill} border border-border`} data-testid="seo-import-toggle"><Upload className="h-4 w-4" /> Import CSV</button>
      </div>
      {imp && <CsvImport onDone={load} />}
      {sel.length > 0 && <BulkBar keys={sel} onDone={() => { setSel([]); load(); }} />}
      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground track-label text-[11px] border-b border-border">
            <th className="p-3"><input type="checkbox" checked={all} onChange={() => setSel(all ? [] : rows.map((r) => r.key))} data-testid="seo-select-all" /></th>
            <th className="p-3">Page</th><th className="p-3">SEO title</th><th className="p-3">Robots</th><th className="p-3">Score</th><th className="p-3">Issues</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-border/50 hover:bg-secondary/40 cursor-pointer" onClick={() => setEdit(r.key)} data-testid={`seo-row-${r.key}`}>
                <td className="p-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.includes(r.key)} onChange={() => setSel(sel.includes(r.key) ? sel.filter((k) => k !== r.key) : [...sel, r.key])} /></td>
                <td className="p-3"><p className="font-medium">{r.name}{r.custom && <span className="ml-2 text-[10px] text-primary">CUSTOM</span>}</p><p className="text-xs text-muted-foreground">{r.type} · {r.path}</p></td>
                <td className="p-3 max-w-xs truncate">{r.effective.title}</td>
                <td className="p-3 text-xs">{r.effective.robots}</td>
                <td className="p-3"><Score v={r.score} /></td>
                <td className="p-3 text-xs text-muted-foreground">{r.issues.length ? r.issues.join(", ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="text-center text-muted-foreground py-10">No pages match.</p>}
      </div>
      {edit && <PageEditor pageKey={edit} onClose={() => setEdit(null)} onSaved={load} />}
    </div>
  );
}
